import React, { useState } from "react";
import FormControl from "react-bootstrap/FormControl";
import { gettext } from "../../i18n";
import "./PhoneNumberFormControl.scss";

interface PhoneNumberFormControlProps {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const PhoneNumberFormControl: React.FC<PhoneNumberFormControlProps> = ({
  value,
  defaultValue,
  placeholder,
  onChange,
  onValidationChange,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue || value || "");
  const [showValidation, setShowValidation] = useState(false);

  // Validate if the phone number has international format
  const isValidInternationalFormat = (phoneNumber: string): boolean => {
    if (!phoneNumber || phoneNumber.trim() === "") {
      return true; // Empty is valid (optional field)
    }
    return phoneNumber.trim().startsWith("+");
  };

  const isValid = isValidInternationalFormat(inputValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowValidation(newValue.trim().length > 0);

    const valid = isValidInternationalFormat(newValue);

    // Notify parent component of validation state
    if (onValidationChange) {
      onValidationChange(valid);
    }

    // Call the original onChange handler
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = () => {
    // Show validation message on blur if there's content
    if (inputValue.trim().length > 0) {
      setShowValidation(true);
    }
  };

  return (
    <div className="phone-number-form-control">
      <FormControl
        type="text"
        value={value !== undefined ? value : inputValue}
        defaultValue={defaultValue}
        placeholder={placeholder || gettext("Enter phone number")}
        onChange={handleChange}
        onBlur={handleBlur}
        className={showValidation && !isValid ? "is-invalid" : ""}
      />
      {showValidation && !isValid && (
        <div className="phone-validation-error">
          {gettext(
            "Phone number must include country code (e.g., +358 for Finland)"
          )}
        </div>
      )}
    </div>
  );
};

export default PhoneNumberFormControl;
